import React, { useEffect, useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { FaSearch } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { formatMoney } from "../../../utils";
import {
  useApproveAdminSmsTopupMutation,
  useGetAdminSmsTopupRequestsQuery,
} from "../../../store/smsMessagingAdminApi";

/** Supports ofPaginatedData + Resource collection (flat data[]) and nested paginator shapes. */
function extractPaginated(res) {
  if (!res) {
    return { rows: [], total: 0, currentPage: 1, lastPage: 1 };
  }

  const nested = res.data;

  if (nested && Array.isArray(nested.data)) {
    return {
      rows: nested.data,
      total: nested.total ?? res.total ?? 0,
      currentPage: nested.current_page ?? res.currentPage ?? 1,
      lastPage: nested.last_page ?? res.lastPage ?? 1,
    };
  }

  if (Array.isArray(nested)) {
    return {
      rows: nested,
      total: res.total ?? nested.length,
      currentPage: res.currentPage ?? 1,
      lastPage: res.lastPage ?? 1,
    };
  }

  return { rows: [], total: 0, currentPage: 1, lastPage: 1 };
}

function statusBadge(status) {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") {
    return "bg-green-100 text-green-800";
  }
  if (s === "PENDING") {
    return "bg-amber-100 text-amber-800";
  }
  return "bg-gray-100 text-gray-700";
}

function formatKoboAsNgn(kobo) {
  if (kobo === null || kobo === undefined || kobo === "") return null;
  return formatMoney((Number(kobo) / 100).toFixed(2), true);
}

function DetailRow({ label, children }) {
  return (
    <div className="border-b border-gray-100 pb-3">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <div className="text-sm font-semibold text-[#383838] break-words">
        {children}
      </div>
    </div>
  );
}

export default function TopUpOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const companyIdParam = searchParams.get("company_id") || "";

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [companyIdParam]);

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetAdminSmsTopupRequestsQuery({
      page,
      search: appliedSearch,
      status: statusFilter,
      companyId: companyIdParam || undefined,
    });
  const [approve, { isLoading: approving }] = useApproveAdminSmsTopupMutation();

  const { rows, total, currentPage, lastPage } = useMemo(
    () => extractPaginated(data),
    [data]
  );

  const loadError =
    isError &&
    (error?.data?.message || error?.message || "Could not load top-up requests.");

  const handleApprove = async (id) => {
    if (
      !window.confirm(
        "Approve this top-up and credit the cooperative branch SMS balance?"
      )
    ) {
      return;
    }
    try {
      await approve({ id }).unwrap();
      toast.success("Top-up approved. Branch SMS balance updated.");
      refetch();
      setSelected(null);
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Approval failed.");
    }
  };

  const clearCompanyFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("company_id");
    setSearchParams(next);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-xl p-4 mb-6 flex items-center">
        <Link to="/sms-messaging" aria-label="Back">
          <BsArrowLeft className="text-xl text-gray-600 mr-4 cursor-pointer" />
        </Link>
        <h1 className="text-2xl font-bold text-[#45464e]">SMS top-up history</h1>
      </div>

      {loadError && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 text-red-600 text-sm">
          {loadError}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <form
          className="relative flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSearch(searchInput.trim());
            setPage(1);
          }}
        >
          <FaSearch className="absolute left-3 text-gray-600 text-xl pointer-events-none top-2.5" />
          <input
            type="search"
            value={searchInput}
            onChange={(ev) => setSearchInput(ev.target.value)}
            placeholder="Filter by cooperative (server search)"
            className="flex-1 min-w-[200px] border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:ring-0 outline-none text-gray-700"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700"
          >
            <option value="all">All statuses</option>
            <option value="APPROVED">Approved / credited</option>
            <option value="PENDING">Pending approval</option>
          </select>
          <button
            type="submit"
            className="bg-[#31778c] text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Search
          </button>
        </form>
        {companyIdParam ? (
          <p className="text-sm text-[#31778c] mt-2">
            Filtered to cooperative ID {companyIdParam}.{" "}
            <button
              type="button"
              onClick={clearCompanyFilter}
              className="underline font-medium"
            >
              Show all cooperatives
            </button>
          </p>
        ) : null}
        <p className="text-gray-500 text-sm mt-2">
          Showing {isLoading ? "…" : total} record
          {total === 1 ? "" : "s"}. Successful online top-ups are credited
          automatically and appear as APPROVED. Pending items (legacy) can still
          be approved manually.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {(isLoading || isFetching) && rows.length === 0 ? (
          <p className="p-6 text-gray-600 text-sm">Loading…</p>
        ) : null}
        {!isLoading && rows.length === 0 ? (
          <p className="p-6 text-gray-600 text-sm">No SMS top-up records found.</p>
        ) : null}

        {rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse">
              <thead>
                <tr className="bg-[#383838] text-white text-left text-sm">
                  <th className="py-3 px-4 font-semibold">Cooperative</th>
                  <th className="py-3 px-4 font-semibold">Amount</th>
                  <th className="py-3 px-4 font-semibold">Balance change</th>
                  <th className="py-3 px-4 font-semibold">Reference</th>
                  <th className="py-3 px-4 font-semibold">Status</th>
                  <th className="py-3 px-4 font-semibold">Date</th>
                  <th className="py-3 px-4 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((order) => {
                  const status = String(order.status || "").toUpperCase();
                  const isPending = status === "PENDING";
                  const beforeLabel = formatKoboAsNgn(order.balance_before_kobo);
                  const afterLabel = formatKoboAsNgn(order.balance_after_kobo);
                  const ref =
                    order.transaction_reference ||
                    order.transaction?.reference ||
                    "—";

                  return (
                    <tr
                      key={order.id}
                      className="border-b border-[#e0e0e0] hover:bg-[#fafafa]"
                    >
                      <td className="py-3 px-4 text-sm">
                        <p className="font-semibold text-[#383838] mb-0">
                          {order.company?.name ?? `Company #${order.company_id}`}
                        </p>
                        <p className="text-xs text-gray-500 mb-0">
                          {order.branch?.name
                            ? `Branch: ${order.branch.name}`
                            : order.branch_id
                            ? `Branch ID: ${order.branch_id}`
                            : "Branch: —"}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-[#19BC31] whitespace-nowrap">
                        {formatMoney(Number(order.amount_paid ?? 0).toFixed(2), true)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                        {beforeLabel && afterLabel ? (
                          <>
                            {beforeLabel} → {afterLabel}
                          </>
                        ) : (
                          <span className="text-gray-400">Not recorded</span>
                        )}
                      </td>
                      <td
                        className="py-3 px-4 text-xs font-mono text-gray-600 max-w-[160px] truncate"
                        title={ref}
                      >
                        {ref}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${statusBadge(
                            status
                          )}`}
                        >
                          {status || "UNKNOWN"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                        {order.created_at
                          ? new Date(order.created_at).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setSelected(order)}
                            className="px-3 py-1.5 text-sm font-medium rounded-md border-2 border-[#8b313a] text-[#8b313a] hover:bg-[rgba(139,49,58,0.06)]"
                          >
                            View
                          </button>
                          {isPending ? (
                            <button
                              type="button"
                              disabled={approving}
                              onClick={() => handleApprove(order.id)}
                              className="px-3 py-1.5 text-sm font-medium rounded-md bg-[#8b313a] text-white hover:bg-[#6b252a] disabled:opacity-50"
                            >
                              Approve
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {lastPage > 1 && (
        <div className="flex justify-between items-center mt-6">
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="text-sm text-[#31778c] disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {lastPage}
          </span>
          <button
            type="button"
            disabled={currentPage >= lastPage}
            onClick={() => setPage((p) => (p >= lastPage ? p : p + 1))}
            className="text-sm text-[#31778c] disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      <Modal
        show={Boolean(selected)}
        onHide={() => setSelected(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-[#8b313a] font-semibold">
            SMS top-up details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailRow label="Cooperative">
                {selected.company?.name ?? `Company #${selected.company_id}`}
              </DetailRow>
              <DetailRow label="Branch">
                {selected.branch?.name ||
                  (selected.branch_id ? `Branch #${selected.branch_id}` : "—")}
              </DetailRow>
              <DetailRow label="Status">
                <span
                  className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${statusBadge(
                    selected.status
                  )}`}
                >
                  {String(selected.status || "UNKNOWN").toUpperCase()}
                </span>
              </DetailRow>
              <DetailRow label="Amount paid">
                {formatMoney(Number(selected.amount_paid ?? 0).toFixed(2), true)}
              </DetailRow>
              <DetailRow label="Credit to wallet">
                {selected.credit_kobo != null
                  ? formatKoboAsNgn(selected.credit_kobo)
                  : selected.credit_ngn != null
                  ? formatMoney(Number(selected.credit_ngn).toFixed(2), true)
                  : "—"}
              </DetailRow>
              <DetailRow label="Request ID">#{selected.id}</DetailRow>
              <DetailRow label="Balance before top-up">
                {formatKoboAsNgn(selected.balance_before_kobo) || (
                  <span className="text-gray-400 font-normal">
                    Not recorded (older top-up)
                  </span>
                )}
              </DetailRow>
              <DetailRow label="Balance after top-up">
                {formatKoboAsNgn(selected.balance_after_kobo) || (
                  <span className="text-gray-400 font-normal">
                    Not recorded (older top-up)
                  </span>
                )}
              </DetailRow>
              <DetailRow label="Transaction ID">
                {selected.transaction_id || selected.transaction?.id || "—"}
              </DetailRow>
              <DetailRow label="Payment reference">
                <span className="font-mono text-xs">
                  {selected.transaction_reference ||
                    selected.transaction?.reference ||
                    "—"}
                </span>
              </DetailRow>
              <DetailRow label="Payment method">
                {selected.transaction?.display_payment_method ||
                  selected.transaction?.payment_method ||
                  "—"}
              </DetailRow>
              <DetailRow label="Payment status">
                {selected.transaction?.status || "—"}
              </DetailRow>
              <DetailRow label="Requested by">
                {selected.requested_by?.name ||
                  (selected.requested_by_staff_id
                    ? `Staff #${selected.requested_by_staff_id}`
                    : "—")}
              </DetailRow>
              <DetailRow label="Date & time">
                {selected.created_at
                  ? new Date(selected.created_at).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })
                  : "—"}
              </DetailRow>
              {selected.transaction?.remark ? (
                <div className="sm:col-span-2">
                  <DetailRow label="Remark">{selected.transaction.remark}</DetailRow>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex justify-end gap-2 mt-5">
            {selected &&
            String(selected.status || "").toUpperCase() === "PENDING" ? (
              <button
                type="button"
                disabled={approving}
                onClick={() => handleApprove(selected.id)}
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-[#8b313a] hover:bg-[#6b252a] disabled:opacity-50"
              >
                Approve &amp; credit
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="px-4 py-2 text-sm font-medium text-white rounded-md bg-[#31778c] hover:bg-[#255a6b]"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
